import { Component, inject, OnInit, signal } from '@angular/core';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { Params } from '../../../services/utilis/add-params-service';
import { Ride } from '../../../models/ride';
import { PhotoStatus, RideStatus } from '../../../models/enums';
import { enumToOptions } from '../../../@shared/utility/enumToOptions';
import { FormControl } from '@angular/forms';
import { RideService } from '../../../services/rides/ride-service';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, EMPTY, map, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { ModelService } from '../../../services/vehicles/model-service';
import { Model } from '../../../models/model';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-rental-rides',
  imports: [TableComponent, CommonModule],
  templateUrl: './rental-rides.html',
  styleUrl: './rental-rides.scss'
})
export class RentalRides implements OnInit {

  params = [
    'page', 'size', 'search', 'sortBy', 'sortDirection',
    'startedFrom', 'startedTo', 'status', 'minRating', 'maxRating',
    'photoApprovalStatus', 'modelId'
  ]

  modifiedParams: Params = {
    page: 'page', size: 'size', search: 'search',
    sortBy: 'sortBy', sortDirection: 'sortDirection',
    startedFrom: 'startedFrom', startedTo: 'startedTo',
    status: 'status', minRating: 'minRating', maxRating: 'maxRating',
    photoApprovalStatus: 'photoApprovalStatus', modelId: 'modelId',
  }

  tableColumns: DynamicColumn<Ride>[] = [
    {
      header: 'Ride', field: 'rideId', sortable: false,
      valueGetter: (r) => '#' + r.rideId,
      bottomField: 'startedAt', isBottomFieldDate: true,
      isCopy: true,
    },
    {
      header: 'Car', field: 'plateNo', sortable: false,
      thumbnailField: 'carImageUrl',
      command: (r) => this.router.navigate(['/vehicles'], { queryParams: { search: r.plateNo }, queryParamsHandling: '' }),
      commandLink: (r) => `/vehicles?search=${encodeURIComponent(r.plateNo as string)}`,
      cellClass: 'car-link-cell',
    },
    {
      header: 'Customer', field: 'customerFirstName', mergedName: 'customerLastName',
      bottomField: 'customerEmail', sortable: true,
      bottomFieldCommand: (r) => this.router.navigate(['/customers'], { queryParams: { search: r.customerEmail } }),
      bottomFieldLink: (r) => `/customers?search=${encodeURIComponent(r.customerEmail as string)}`,
    },
    { header: 'Phone', field: 'customerPhoneNumber', sortable: false },
    { header: 'Started', field: 'startedAt', sortable: true, isTime: true },
    { header: 'Ended', field: 'endedAt', sortable: true, isTime: true },
    { header: 'Duration', field: 'durationSeconds', sortable: false, isDuration: true },
    { header: 'Distance', field: 'distanceKm', sortable: false, suffixText: 'km' },
    { header: 'Collected', field: 'totalCollectedAmount', sortable: false, isCurrency: true },
    {
      header: 'Status', field: 'status', sortable: true, isStatusBadge: true,
      badgeStyleGetter: (v) => this.rideStatusBadge(v as string),
    },
    {
      header: 'Photo Status', field: 'photoApprovalStatus', sortable: false, isStatusBadge: true,
      badgeStyleGetter: (v) => this.photoStatusBadge(v as string),
    },
    { header: 'Feedback', field: 'feedbackRating', sortable: false, isRating: true },
    { header: 'Comment', field: 'feedbackComment', sortable: false },
  ]

  ridesList = signal<Ride[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  searchControl = new FormControl('');

  rideStatusFilter: CustomFilter = {
    key: 'status', label: 'Ride Status', type: 'multiselect',
    options: enumToOptions(RideStatus),
  }
  photoStatusFilter: CustomFilter = {
    key: 'photoApprovalStatus', label: 'Photo Status', type: 'multiselect',
    options: enumToOptions(PhotoStatus),
  }
  startedDateFilter: CustomFilter = { key: 'started', label: 'Started Date', type: 'date-range', options: [] }
  modelsFilter: CustomFilter = { key: 'modelId', label: 'Model', type: 'multiselect', options: [] }

  private rideService = inject(RideService)
  private modelService = inject(ModelService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getModels();
    this.getRidesList();
  }

  getRidesList = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
          .filter(([key]) => this.params.includes(key) || key === 'tab')
          .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          rentalType: 'CAR_RENTAL',
          sortBy: queryParams['sortBy'] ?? 'startedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        if(!queryParams['tab'] || queryParams['tab'] === 'RIDE_RENTAL') {
          return this.rideService.getRidesList(params);
        }
        else return EMPTY;
      })
    ).subscribe((res) => {
      this.ridesList.set(res.data.content);
      this.totalRecords.set(res.data.totalElements);
      this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize;
    });
  }

  getModels() {
    this.modelService.getModelsForDropdown().subscribe({
      next: (res) => {
        this.modelsFilter.options = res.data.map((m: Model) => ({ label: m.name, value: m.id }));
      }
    });
  }

  rideStatusBadge(value: string): { background: string; color: string } {
    const map: Record<string, { background: string; color: string }> = {
      'ACTIVE':               { background: 'var(--status-active-bg, #dcfce7)', color: 'var(--status-active-text, #16a34a)' },
      'COMPLETED':            { background: 'var(--status-completed-bg, #dbeafe)', color: 'var(--status-completed-text, #1d4ed8)' },
      'COMPLETED_WITH_DEBT':  { background: '#fee2e2', color: '#dc2626' },
      'CANCELLED':            { background: 'var(--status-cancelled-bg, #f1f5f9)', color: 'var(--status-cancelled-text, #64748b)' },
    };
    return map[value] ?? { background: '#f1f5f9', color: '#64748b' };
  }

  photoStatusBadge(value: string): { background: string; color: string } {
    const map: Record<string, { background: string; color: string }> = {
      'APPROVED': { background: 'var(--status-approved-bg, #dcfce7)', color: 'var(--status-approved-text, #16a34a)' },
      'REJECTED': { background: 'var(--status-cancelled-bg, #f1f5f9)', color: 'var(--status-cancelled-text, #64748b)' },
      'PENDING':  { background: 'var(--status-pending-bg, #fef9c3)', color: 'var(--status-pending-text, #854d0e)' },
    };
    return map[value] ?? { background: '#f1f5f9', color: '#64748b' };
  }

  onPageChange = (event: PaginatorState) => {
    this.router.navigate([], {
      queryParams: { page: event.page, size: event.rows },
      queryParamsHandling: 'merge',
    });
  }

  redirectToRideDetailsPage = (ride: Ride) => this.router.navigate([`/rides/${ride.rideId}`]);
  rideRowLink = (ride: Ride) => `/rides/${ride.rideId}`;
}
